/**
 * Storage Initialization Hook
 * 
 * Initializes Phase 4 advanced persistence functionality at app startup.
 * Handles automatic migration and sets up reactive storage patterns.
 * 
 * Phase 4 implementation - complete persistence system initialization
 */

import { useEffect, useState } from 'react';
import { useStorageMigration } from './useNamespacedStorage';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Hook to initialize Phase 4 storage system
 */
export function useStorageInitialization() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState<string | null>(null);
  
  // Use the migration hook
  const {
    migrationStatus,
    migrationResult,
    needsMigration,
    runMigration
  } = useStorageMigration();

  useEffect(() => {
    const initializeStorage = async () => {
      try {
        if (isDevelopment) {
          console.log('StorageInitialization: Starting Phase 4 initialization');
          performance.mark('storage-initialization-start');
        }

        // Wait for migration to complete
        if (migrationStatus === 'running') {
          // Migration is already in progress, wait for it
          return;
        }

        if (migrationStatus === 'error') {
          setInitializationError('Storage migration failed');
          return;
        }

        // If migration was successful or not needed, mark as initialized
        if (migrationStatus === 'completed') {
          setIsInitialized(true);
          setInitializationError(null);

          if (isDevelopment) {
            if (performance.getEntriesByName('storage-initialization-start').length > 0) {
              performance.measure('storage-initialization', 'storage-initialization-start');
              const measurement = performance.getEntriesByName('storage-initialization')[0];
              console.log('StorageInitialization: Completed in', measurement?.duration.toFixed(2), 'ms');
            }

            if (migrationResult) {
              console.log('StorageInitialization: Migration summary', {
                migrated: migrationResult.migrated.length,
                errors: migrationResult.errors.length,
                hadMigration: migrationResult.migrated.length > 0
              });
            }
          }
        }
      } catch (error) {
        console.error('StorageInitialization: Failed to initialize storage system:', error);
        setInitializationError(`Initialization failed: ${error}`);
      }
    };

    initializeStorage();
  }, [migrationStatus, migrationResult]);

  return {
    isInitialized,
    initializationError,
    migrationStatus,
    migrationResult,
    needsMigration: needsMigration(),
    
    // Computed properties for easy consumption
    isLoading: migrationStatus === 'running' || (!isInitialized && !initializationError),
    hasError: !!initializationError || migrationStatus === 'error',
    
    // Development utilities
    ...(isDevelopment && {
      devUtils: {
        forceReinitialize: runMigration,
      }
    })
  };
}

/**
 * Storage readiness context for components that depend on Phase 4 storage
 */
export function useStorageReady() {
  const { isInitialized, isLoading, hasError } = useStorageInitialization();
  
  return {
    ready: isInitialized && !isLoading && !hasError,
    loading: isLoading,
    error: hasError
  };
}